// Prose primitives for the plugin docs pages: anchored headings, paragraphs,
// tables, code blocks, callouts, and the risk-label badge shared with the
// marketplace page. Typography stays on the site's tokens.

import type { ReactNode } from 'react';

export function H2({ id, children }: { id: string; children: ReactNode }) {
  return (
    <h2
      id={id}
      className="group scroll-mt-24 text-lg font-semibold text-textPrimary mt-10 mb-3 first:mt-0"
    >
      <a href={`#${id}`} className="hover:text-accent transition-colors">
        {children}
        <span className="ml-2 text-accent opacity-0 group-hover:opacity-60 transition-opacity">
          #
        </span>
      </a>
    </h2>
  );
}

export function H3({ id, children }: { id?: string; children: ReactNode }) {
  return (
    <h3 id={id} className="scroll-mt-24 text-[15px] font-semibold text-textPrimary mt-6 mb-2">
      {children}
    </h3>
  );
}

export function P({ children }: { children: ReactNode }) {
  return <p className="text-[14px] text-textSecondary leading-relaxed mb-3">{children}</p>;
}

export function Ul({ children }: { children: ReactNode }) {
  return (
    <ul className="list-disc list-outside pl-5 space-y-1.5 marker:text-textMuted text-[14px] text-textSecondary leading-relaxed mb-4">
      {children}
    </ul>
  );
}

export function Ol({ children }: { children: ReactNode }) {
  return (
    <ol className="list-decimal list-outside pl-5 space-y-1.5 marker:text-textMuted text-[14px] text-textSecondary leading-relaxed mb-4">
      {children}
    </ol>
  );
}

export function Code({ children }: { children: ReactNode }) {
  return (
    <code className="px-1.5 py-0.5 rounded bg-white/[0.06] border border-white/[0.06] font-mono text-[12px] text-textPrimary">
      {children}
    </code>
  );
}

export function CodeBlock({ code }: { code: string }) {
  return (
    <pre className="rounded-lg border border-white/[0.06] bg-black/40 p-4 mb-4 overflow-x-auto font-mono text-[12px] leading-relaxed text-textSecondary">
      <code>{code}</code>
    </pre>
  );
}

export function DocTable({ head, rows }: { head: string[]; rows: ReactNode[][] }) {
  return (
    <div className="overflow-x-auto rounded-lg border border-white/[0.06] mb-4">
      <table className="w-full text-[13px] border-collapse">
        <thead>
          <tr className="bg-white/[0.03] text-left">
            {head.map(h => (
              <th key={h} className="px-3 py-2 font-medium text-textPrimary whitespace-nowrap">
                {h}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {rows.map((r, i) => (
            <tr key={i} className="border-t border-white/[0.06] align-top">
              {r.map((c, j) => (
                <td key={j} className="px-3 py-2 text-textSecondary leading-relaxed">
                  {c}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

export function Callout({
  tone = 'info',
  children,
}: {
  tone?: 'info' | 'warning';
  children: ReactNode;
}) {
  const toneClass =
    tone === 'warning'
      ? 'border-amber-400/40 bg-amber-500/[0.06]'
      : 'border-[var(--color-accent)]/40 bg-white/[0.03]';
  return (
    <div
      className={`border-l-2 ${toneClass} rounded-r-lg px-4 py-3 mb-4 text-[13px] text-textSecondary leading-relaxed`}
    >
      {children}
    </div>
  );
}

export type RiskTier = 'A' | 'B' | 'C';

export const TIER_LABELS: Record<RiskTier, string> = {
  A: 'Standard',
  B: 'Extended',
  C: 'Advanced',
};

const TIER_CLASSES: Record<RiskTier, string> = {
  A: 'bg-emerald-500/20 text-emerald-200',
  B: 'bg-sky-500/20 text-sky-200',
  C: 'bg-violet-500/20 text-violet-200',
};

export function RiskBadge({ tier }: { tier: RiskTier }) {
  return (
    <span
      className={`inline-flex items-center whitespace-nowrap rounded-full px-2.5 py-0.5 text-[11px] font-semibold ${TIER_CLASSES[tier]}`}
    >
      {TIER_LABELS[tier]}
    </span>
  );
}
